import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Firestore } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { BaseCrudService } from '@core/services/data/base-crud.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { AuthService } from '@core/services/auth/auth.service';
import { InventoryReport } from '@shared/definitions/inventory-report';
import { InventoryReportStore } from '@shared/services/inventory-report.store';

/**
 * CRUD service for inventory reports ("état des lieux"), backed by the
 * workspace-scoped `inventoryReports` collection. Bridges its entities stream
 * into `InventoryReportStore` so consumers read via the signal API.
 */
@Injectable({ providedIn: 'root' })
export class InventoryReportService extends BaseCrudService<InventoryReport> {
  private readonly store = inject(InventoryReportStore);
  private readonly functions = inject(Functions);

  constructor(
    firestore: Firestore,
    pathService: FirestorePathService,
    authService: AuthService
  ) {
    super(firestore, pathService, authService, {
      collectionName: 'inventoryReports',
      idField: 'reportUID',
      enableOptimisticUpdates: true
    });

    // Read-path bridge → store signal (same pattern as ContractsService).
    this.entitiesSubject.pipe(takeUntilDestroyed(this.crudDestroyRef)).subscribe(list => this.store.setList(list));
  }

  /**
   * Render the report to an A4 HTML document (server-side) and persist it on the
   * report. Returns the HTML for immediate preview / download. The document is
   * the print-ready état des lieux registered (free) together with the lease.
   */
  async generateDocument(
    workspaceId: string,
    reportUID: string,
    language?: string
  ): Promise<{ html: string; fileName: string; language: string }> {
    const fn = httpsCallable<
      { workspaceId: string; reportUID: string; language?: string },
      { success: boolean; html: string; fileName: string; language: string }
    >(this.functions, 'generateInventoryReportDocument');
    const res = await fn({ workspaceId, reportUID, ...(language ? { language } : {}) });
    return { html: res.data.html, fileName: res.data.fileName, language: res.data.language };
  }
}
